import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowRight } from 'lucide-react';
import { ROLES } from './smb-beta-data';

/* Props:
 *   onSuccess — called when the API call succeeds; parent handles success screen
 */
const SMBBetaForm = ({ onSuccess }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    email: '',
    role: '',
    challenge: '',
  });
  const [utmParams, setUtmParams] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formStarted, setFormStarted] = useState(false);

  useEffect(() => {
    const params = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(key => {
      const value = searchParams.get(key);
      if (value) params[key] = value;
    });
    setUtmParams(params);
  }, [searchParams]);

  const handleFormStart = () => {
    if (!formStarted && window.posthog) {
      window.posthog.capture('early_access_form_started', {
        form_type: 'smb_beta',
        utm_source: searchParams.get('utm_source') || undefined,
        utm_campaign: searchParams.get('utm_campaign') || undefined,
        utm_medium: searchParams.get('utm_medium') || undefined,
      });
      setFormStarted(true);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        type: 'smb',
        ...utmParams,
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/early-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || t('smb.form.errorGeneric'));
      }

      // Track successful form submission
      if (window.posthog) {
        window.posthog.capture('early_access_form_submitted', {
          form_type: 'smb_beta',
          utm_source: searchParams.get('utm_source') || undefined,
          utm_campaign: searchParams.get('utm_campaign') || undefined,
          utm_medium: searchParams.get('utm_medium') || undefined,
        });
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-lg p-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-2 text-center">
            {t('smb.form.title')}
          </h2>
          <p className="text-slate-600 mb-8 text-center">
            {t('smb.form.subtitle')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="company_name">{t('smb.form.companyName')}</Label>
              <Input
                id="companyName"
                name="companyName"
                type="text"
                required
                value={formData.companyName}
                onChange={handleChange}
                onFocus={handleFormStart}
                placeholder={t('smb.form.companyNamePlaceholder')}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="contact_name">{t('smb.form.contactName')}</Label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder={t('smb.form.contactNamePlaceholder')}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="email">{t('smb.form.email')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder={t('smb.form.emailPlaceholder')}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="role">{t('smb.form.role')}</Label>
              <select
                id="role"
                name="role"
                required
                value={formData.role}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">{t('smb.form.rolePlaceholder')}</option>
                {ROLES.map(({ value, labelKey }) => (
                  <option key={value} value={value}>{t(labelKey)}</option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="challenge">
                {t('smb.form.challenge')}
                <span className="text-slate-500 text-sm ml-2">({t('smb.form.optional')})</span>
              </Label>
              <Textarea
                id="challenge"
                name="challenge"
                value={formData.challenge}
                onChange={handleChange}
                placeholder={t('smb.form.challengePlaceholder')}
                className="mt-1 min-h-[100px]"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground text-lg py-6 group"
            >
              {loading ? t('smb.form.submitting') : t('smb.form.cta')}
              {!loading && <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />}
            </Button>

            <p className="text-xs text-slate-500 text-center">
              {t('smb.form.disclaimer')}
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default SMBBetaForm;
